import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Checkbox,
  Button,
  Typography,
  CircularProgress,
  Popover,
  InputAdornment,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { styles } from "./ColumnFilter.styles";

const ColumnFilter = ({ column, onFilterChange, filters = [] }) => {
  const theme = useTheme();
  const componentStyles = styles(theme);

  const [anchorEl, setAnchorEl] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState({});
  const [filterList, setFilterList] = useState([]);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const open = Boolean(anchorEl);

  // Mirror the applied filters prop — including when it is cleared externally,
  // otherwise the checkboxes and the active-icon state keep stale selections.
  const appliedFilterKey = JSON.stringify(filters || []);
  useEffect(() => {
    const applied = JSON.parse(appliedFilterKey);
    setSelectedItems(
      applied.reduce((acc, filterId) => {
        acc[filterId] = true;
        return acc;
      }, {})
    );
    setIsFilterActive(applied.length > 0);
  }, [appliedFilterKey]);
  const filterConfig = column.filterConfig;

  useEffect(() => {
    if (!filterConfig) {
      setFilterList([]);
      return;
    }

    if (filterConfig.filterOptions) {
      setFilterList(filterConfig.filterOptions);
    } else if (filterConfig.getFilterOptions) {
      setIsLoading(true);
      const fetchOptions = async () => {
        try {
          const options = await filterConfig.getFilterOptions(column.field);
          setFilterList(options);
        } catch (error) {
          console.error("Error fetching filter options:", error);
          setFilterList([]);
        } finally {
          setIsLoading(false);
        }
      };
      fetchOptions();
    } else {
      setFilterList([]);
    }
  }, [filterConfig, column.field]);

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allSelected = filterList.reduce((acc, item) => {
        acc[item.id] = true;
        return acc;
      }, {});
      setSelectedItems(allSelected);
    } else {
      setSelectedItems({});
    }
  };

  const handleItemSelect = (item, checked) => {
    setSelectedItems((prev) => {
      const newSelected = { ...prev };
      if (checked) {
        newSelected[item.id] = true;
      } else {
        delete newSelected[item.id];
      }
      return newSelected;
    });
  };

  const handleApplyFilter = () => {
    const selectedValues = Object.keys(selectedItems);
    setIsFilterActive(selectedValues.length > 0);

    if (onFilterChange) {
      onFilterChange(column.field, selectedValues);
    }

    handleFilterClose();
  };

  const handleResetFilter = () => {
    setSelectedItems({});
    setIsFilterActive(false);

    if (onFilterChange) {
      onFilterChange(column.field, []);
    }

    handleFilterClose();
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const filteredList = filterList.filter((item) =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isAllSelected =
    filterList.length > 0 &&
    Object.keys(selectedItems).length === filterList.length;

  return (
    <>
      <IconButton
        size="small"
        onClick={handleFilterClick}
        sx={{
          ...componentStyles.filterButton,
          backgroundColor: isFilterActive
            ? theme.palette.grey[200]
            : "transparent",
          "&:hover": {
            backgroundColor: isFilterActive
              ? theme.palette.grey[300]
              : theme.palette.grey[100],
          },
        }}
      >
        <FilterListIcon fontSize="small" />
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: componentStyles.popover }}
      >
        {/* HEADER */}
        <Box sx={componentStyles.header}>
          <TextField
            size="small"
            placeholder="Search options..."
            value={searchQuery}
            onChange={handleSearchChange}
            fullWidth
            variant="outlined"
            sx={componentStyles.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={componentStyles.searchIcon} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* CONTENT */}
        <Box sx={componentStyles.content}>
          {isLoading ? (
            <Box sx={componentStyles.loadingContainer}>
              <CircularProgress size={20} sx={componentStyles.loadingSpinner} />
            </Box>
          ) : filterList.length === 0 ? (
            <Box sx={componentStyles.emptyState}>
              <Typography sx={componentStyles.emptyStateText}>
                No options available
              </Typography>
            </Box>
          ) : (
            <>
              {/* SELECT ALL */}
              {/* <Box onClick={() => handleSelectAll(!isAllSelected)} sx={componentStyles.selectAllContainer}>
                 <Checkbox
                   size="small"
                   checked={isAllSelected}
                   indeterminate={Object.keys(selectedItems).length > 0 && !isAllSelected}
                   sx={componentStyles.selectAllCheckbox}
                 />
                 <Typography sx={componentStyles.selectAllText}>
                   Select All
                 </Typography>
               </Box> */}

              {/* OPTIONS */}
              {filteredList.map((item) => (
                <Box
                  key={item.id}
                  onClick={() =>
                    handleItemSelect(item, !selectedItems[item.id])
                  }
                  sx={componentStyles.optionContainer}
                >
                  <Checkbox
                    size="small"
                    checked={Boolean(selectedItems[item.id])}
                    sx={componentStyles.optionCheckbox}
                  />
                  <Typography sx={componentStyles.optionText}>
                    {item.label}
                  </Typography>
                </Box>
              ))}

              {filteredList.length === 0 && searchQuery && (
                <Box sx={componentStyles.noResultsContainer}>
                  <Typography sx={componentStyles.noResultsText}>
                    No items found
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* FOOTER */}
        <Box sx={componentStyles.footer}>
          <Box sx={componentStyles.buttonContainer}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleResetFilter}
              sx={componentStyles.resetButton}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyFilter}
              sx={componentStyles.applyButton}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ColumnFilter;
